/**
 * DocumentSectionList - Sequential vertical layout for document sections
 * Sections flow top-to-bottom following the document's content[] order.
 * No free-form canvas positioning - follows the unified block architecture
 * where documents use layout mode 'column'.
 */

import React, { useCallback } from 'react'
import { CallSheetSection } from './DocumentSection'
import type { SectionItem, PlayItem } from './Types'
import './document-editor.css'

interface DocumentSectionListProps {
	sections: SectionItem[]
	plays: PlayItem[]
	zoomLevel: number
	showGrid: boolean
	selectedSectionId: string | null
	onSelectSection: (sectionId: string | null) => void
	onMoveSection: (sectionId: string, position: { x: number; y: number }) => void
	onAddPlayToSection: (sectionId: string, playId: string, columnIndex: number, rowIndex: number) => void
	onRemovePlay: (playId: string) => void
}

export function CallSheetCanvas({
	sections,
	plays,
	showGrid,
	selectedSectionId,
	onSelectSection,
	onAddPlayToSection,
	onRemovePlay,
}: DocumentSectionListProps) {
	// Handle drop from play library
	const handleSectionDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault()
		e.dataTransfer.dropEffect = 'copy'
	}, [])

	const handleSectionDrop = useCallback((sectionId: string) => {
		return (e: React.DragEvent, columnIndex: number, rowIndex: number) => {
			e.preventDefault()
			const playId = e.dataTransfer.getData('application/play-id')
			if (playId) {
				onAddPlayToSection(sectionId, playId, columnIndex, rowIndex)
			}
		}
	}, [onAddPlayToSection])

	return (
		<div
			className="document-section-list"
			onClick={() => onSelectSection(null)}
		>
			{/* Grid overlay for visual alignment */}
			{showGrid && (
				<div className="document-section-list__grid-overlay" />
			)}

			{/* Sequential sections */}
			{sections.length === 0 ? (
				<div className="document-section-list__empty">
					<p>No sections yet.</p>
					<p>Click the + button in the toolbar to add a section.</p>
				</div>
			) : (
				sections.map(section => {
					// Get plays that belong to this section by matching children
					const sectionPlays = plays.filter(p => {
						// Match plays whose blockId is in this section's children
						return section.children.some(
							child => child.type === 'play_reference' && child.block.id === p.blockId
						)
					})

					return (
						<div
							key={section.id}
							className={`document-section-list__item ${
								selectedSectionId === section.id ? 'document-section-list__item--selected' : ''
							}`}
							onClick={(e) => {
								e.stopPropagation()
								onSelectSection(section.id)
							}}
						>
							<CallSheetSection
								section={section}
								plays={sectionPlays}
								isSelected={selectedSectionId === section.id}
								dockedEdges={{}}
								onSelect={() => onSelectSection(section.id)}
								onAddPlay={(_col: number, _row: number) => {
									// Quick-add from section cell - future enhancement
								}}
								onRemovePlay={onRemovePlay}
								onAddColumn={() => {/* Future enhancement */}}
								onAddRow={() => {/* Future enhancement */}}
								onDragOver={handleSectionDragOver}
								onDrop={handleSectionDrop(section.id)}
							/>
						</div>
					)
				})
			)}
		</div>
	)
}
